import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  Select,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { openDeleteModal } from "@components/DeleteModal";
import { ScrollArea } from "@components/ScrollArea";

import { handleOnChange } from "@utils/handleOnChange";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getLabsStatuses } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_AFTER_UPLOAD,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
  ROLES,
} from "@constants/index";

import { useDeleteRadiologyFile } from "@hooks/mutations/radiology/useDeleteRadiologyFile";
import { useUpdateRadiology } from "@hooks/mutations/radiology/useUpdateRadiology";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { Radiology } from "@customTypes/radiology";


interface EditDialogProps {
  radTypes: ComboboxItem[];
  radiology: Partial<Radiology>;
  opened: boolean;
  close: () => void;
}

export const EditModal = ({
  radTypes,
  radiology,
  opened,
  close,
}: EditDialogProps) => {
  const { t } = useTranslation();
  const { token, id: userId, fullName, roles , can } = useAuth();
  const { patientId } = useParams();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [radType, setRadType] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [formData, setFormData] = useState(radiology);
  const [fileIndex, setFileIndex] = useState(-1);
  const [radFiles, setRadFiles] = useState<string[]>(
    radiology.files as string[]
  );
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>();

  const { mutate: updateRadiology, isLoading: isUpdateRadLoading } =
    useUpdateRadiology();
  const { mutate: deleteRadFile, isLoading: isDeleteRadFileLoading } =
    useDeleteRadiologyFile();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({ permission: "radiology.create" });

  const {
    data: supportStaffData,
    isLoading: isSupportStaffLoading,
    isRefetching: isSupportStaffRefetching,
  } = useGetUsers({ permission: "radiology.edit" });

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);
  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdateRadiology = () => {
    const jsFormData = new FormData();

    uploadedFiles.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });

    jsFormData.append(
      "ordered_by",
      doctor?.label === (fullName as string) ? userId : doctor?.value ?? ""
    );
    jsFormData.append("aid", supportStaff?.value ?? "");
    jsFormData.append("rad_type", radType ?? "");
    jsFormData.append("status", status ?? "");
    jsFormData.append("reason", formData?.reason ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    updateRadiology(
      {
        radiology: jsFormData,
        radId: formData.id as string,
        patientId: patientId as string,
      },
      {
        onSuccess: () => {
          close();
          setUploadedFiles([]);
        },
      }
    );
  };

  useEffect(() => {
    if (radiology) {
      setDoctor({
        label: radiology.orderedBy?.fullName as string,
        value: radiology.orderedBy?.id as string,
      });
      setSupportStaff({
        label: radiology.supportStaff?.fullName as string,
        value: radiology.supportStaff?.id as string,
      });
      setStatus(radiology.status as string);
      setRadType(radiology.radType!);
      setFormData(prev => ({
        ...prev,
        reason: radiology.reason,
        id: radiology.id,
      }));
    }
  }, [radiology]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={<Text fw={500}>{t("edit-radiology")}</Text>}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="previous-files">{t("previous-files")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <div className="form-field">
                <Select
                  label={t("radiology-type")}
                  placeholder={t("select-radiology-type")}
                  data={radTypes ?? []}
                  value={radType ?? null}
                  onChange={setRadType}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  disabled={!can("radiology.assign")}
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("support-staff")}
                  placeholder={t("select-support-staff")}
                  data={supportStaffComboboxItems || []}
                  value={supportStaff?.value ?? null}
                  onChange={(_val, option) => setSupportStaff(option)}
                  rightSection={
                    isSupportStaffLoading || isSupportStaffRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  searchable
                />
              </div>

              <div className="form-field">
                <Select
                  withAsterisk
                  label={t("status")}
                  placeholder={t("select-status")}
                  data={getLabsStatuses()}
                  value={status}
                  onChange={setStatus}
                />
              </div>

              <div className="form-field">
                <label htmlFor="reason">{t("reason")}</label>
                <textarea
                  rows={5}
                  placeholder={t("type-reason")}
                  id="reason"
                  name="reason"
                  value={formData?.reason ?? ""}
                  onChange={e =>
                    handleOnChange(e, setFormData, formData)
                  }></textarea>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="previous-files">
            <div className="files-wrapper">
              <div className="uploaded-files">
                <h4>{t("previously-uploaded-files")}</h4>
                <ScrollArea
                  scrollbarOrientation="vertical"
                  className={`previous-files ${
                    radiology.files?.length && radiology.files.length > 6
                      ? "show-gradient"
                      : ""
                  }`}>
                  {radFiles?.length ? (
                    <ul>
                      {radFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH_AFTER_UPLOAD
                              )}
                            </a>
                          </div>

                          <Tooltip label={t("delete-rad-file")} withArrow>
                            <PermissionGate permission="radiology.delete"><ActionIcon
                              color="red"
                              loading={
                                isDeleteRadFileLoading && fileIndex === index
                              }
                              variant="outline"
                              onClick={() => {
                                setFileIndex(index);
                                openDeleteModal({
                                  textNode: (
                                    <Text fz={"sm"}>
                                      {t("delete-rad-file-message")}
                                    </Text>
                                  ),
                                  onConfirm: () => {
                                    if (radiology?.id) {
                                      deleteRadFile(
                                        {
                                          radId: radiology.id,
                                          fileName: (file as string).split(
                                            "/"
                                          )[3],
                                          patientId: patientId as string,
                                        },
                                        {
                                          onSuccess: () =>
                                            setRadFiles(
                                              radFiles.filter(
                                                (_, idx) => idx !== index
                                              )
                                            ),
                                        }
                                      );
                                    }
                                  },
                                });
                              }}>
                              <IconTrash size={16} />
                            </ActionIcon></PermissionGate>
                          </Tooltip>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty-label">
                      {t("empty-previously-uploaded-files")}
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollArea
                    scrollbarOrientation="vertical"
                    className={`uploaded-files ${
                      uploadedFiles.length > 2 ? "show-gradient" : ""
                    }`}>
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            color="red"
                            variant="outline"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt="xl" pb="md">
          <PermissionGate permission="radiology.edit"><Button onClick={handleUpdateRadiology} loading={isUpdateRadLoading}>
            {t("update-radiology")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
